import React, { useState } from 'react';
import { Button, createTheme, ThemeProvider } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';


const theme = createTheme({
    palette: {
        dark_yellow: {
            main: "#393D44",
            contrastText: "#FAEF9B"
        },
        dark: {
            main: "#393D44",
            contrastText: "#393D44"
        }
    }
});

export default function ComparisonCard({
    oldBooking = {},
    newBooking = {},
    totalCHF,
    promoCode = "",
    onPromoChange = () => {},
    isCguChecked,
    onCguToggle = () => {},
    onPromoValidate = () => {},
    onConfirm,
    onCancel,
    footer,
}) {
    const { i18n } = useTranslation();
    const [openSections, setOpenSections] = useState({
        before: true,
        after: true,
    });

    const toggleSection = section => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    const fmtDate = d => (d ? d.split("T")[0] : "—");
    const fmtTime = t => t || "—";
    const listProd = b =>
        (Array.isArray(b?.products) ? b.products : [b?.product])
            .map(p => p?.name)
            .join(", ");
    const price = b =>
        (Array.isArray(b?.products) ? b.products : [b?.product]).reduce(
            (s, p) => s + (p?.price || 0),
            0
        ) + " CHF";
    const therapist = b =>
        (Array.isArray(b?.therapists) ? b.therapists : [b?.therapist])
            .filter(Boolean)
            .map(t => t.firstname)
            .join(", ");
    const addr = a =>
        `${a?.street ?? ""} ${a?.street_number ?? ""}  ${a?.zip_code ?? ""} ${a?.city ?? a?.state ?? ""}`.trim();

    const rows = [
        { label: "Date", before: fmtDate(oldBooking.date), after: fmtDate(newBooking.date) },
        { label: "Heure", before: fmtTime(oldBooking.time), after: fmtTime(newBooking.time) },
        { label: "Produit", before: listProd(oldBooking), after: listProd(newBooking) },
        { label: "FlyPro", before: therapist(oldBooking), after: therapist(newBooking) },
        { label: "Adresse", before: addr(oldBooking.address), after: addr(newBooking.address) },
        { label: "Prix", before: price(oldBooking), after: price(newBooking) },
        { label: "DUO", before: oldBooking.isDuo ? "Oui" : "Non", after: newBooking.duo ? "Oui" : "Non" }
    ];

    const bookingTotal = b =>
        Array.isArray(b?.products)
            ? b.products.reduce((s, p) => s + (p?.price || 0), 0)
            : (b?.product?.price || 0);
    
    const urlsFly = {
        cgu: i18n.language === 'fr' 
        ? 'https://flyspa.ch/conditions-generales-utilisation/' 
        : 'https://flyspa.ch/en/general-terms-of-use/',
        privacy: i18n.language === 'fr' 
        ? 'https://flyspa.ch/politique-de-confidentialite/' 
        : 'https://flyspa.ch/en/privacy-policy/'
    };

    return (
        <div className="comparison-card">
            {/* top selectors */}
            <div className="cmp-select-row">
                <button className="cmp-select">Avant <KeyboardArrowDownIcon /></button>
                <button className="cmp-select">Après <KeyboardArrowDownIcon /></button>
            </div>

            {/* grid */}
            <ul className="cmp-grid">
                {rows.map(r => (
                    <li key={r.label} className="cmp-row">
                        <span className="before"><span className="title-info">{r.label} :</span> <span className="content-info">{r.before}</span></span>
                        <span className="mid-arrow-sep"></span>
                        <span className="after">{r.after}</span>
                        <span className="row-label">{r.label}</span>
                    </li>
                ))}
            </ul>

            <div className="cmp-mobile-accordion">
                <section className="cmp-mobile-section">
                    <button
                        type="button"
                        className={`cmp-mobile-toggle ${openSections.before ? 'is-open' : ''}`}
                        aria-expanded={openSections.before}
                        onClick={() => toggleSection('before')}
                    >
                        Avant <KeyboardArrowDownIcon />
                    </button>
                    {openSections.before && (
                        <ul className="cmp-mobile-panel">
                            {rows.map(r => (
                                <li key={`before-${r.label}`} className="cmp-mobile-item">
                                    <span className="title-info">{r.label} :</span>
                                    <span className="content-info">{r.before}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="cmp-mobile-section">
                    <button
                        type="button"
                        className={`cmp-mobile-toggle ${openSections.after ? 'is-open' : ''}`}
                        aria-expanded={openSections.after}
                        onClick={() => toggleSection('after')}
                    >
                        Après <KeyboardArrowDownIcon />
                    </button>
                    {openSections.after && (
                        <ul className="cmp-mobile-panel">
                            {rows.map(r => (
                                <li key={`after-${r.label}`} className="cmp-mobile-item">
                                    <span className="title-info">{r.label} :</span>
                                    <span className="content-info">{r.after}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            <div className="cmp-footer">
                {footer || (
                    <>
                        <div className="cmp-total-row">
                            <span>Total (TVA incluse)</span>
                            <strong>{totalCHF ?? bookingTotal(newBooking)} CHF</strong>
                        </div>

                        <div className="form-code-promo">
                            <input
                                type="text"
                                placeholder="Indiquer un code promo"
                                value={promoCode}
                                onChange={e => onPromoChange(e.target.value)}
                                className="form-5"
                            />
                            <Button
                                variant="outlined"
                                color="dark"
                                onClick={onPromoValidate}
                                sx={{ textTransform: "none", borderRadius: "20px", minWidth: 90 }}
                            >
                                Valider
                            </Button>
                        </div>

                        <div className="checkbox-2">
                            <input
                                id="cmp-cgu"
                                type="checkbox"
                                className="checkbox"
                                checked={isCguChecked}
                                onChange={e => onCguToggle(e.target.checked)}
                            />
                            <label htmlFor="cmp-cgu" className="accepter-les-CGU-et">
                                <span>J’accepte les </span>
                                <a
                                    href={urlsFly.cgu}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'underline' }}
                                >
                                    conditions générales
                                </a>
                                <span> et la </span>
                                <a
                                    href={urlsFly.privacy}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'underline' }}
                                >
                                    politique de confidentialité
                                </a>
                                <span> *</span>
                            </label>
                        </div>
                    </>
                )}
            </div>

            {/* actions */}

            <div className="rectangleDiv" style={{ gap: 15 }}>
                <ThemeProvider theme={theme}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        sx={{
                            width: "min(260px, 100%)",
                            height: 57,
                            fontSize: 18,
                            fontFamily: "MarcellusRegular, serif",
                            textTransform: "none",
                            borderRadius: 4,
                            border: "1.5px solid #393D44",
                            color: "#393D44",
                            background: "#FFFFFF",
                            boxShadow: "none",
                            "&:hover": {
                                background: "#FFFFFF",
                                boxShadow: "none",
                            },
                        }}
                    >
                        Annuler
                    </Button>

                    {/* Confirmer la modification */}
                    <Button
                        variant="contained"
                        onClick={onConfirm}
                        sx={{
                            width: "min(260px, 100%)",
                            height: 57,
                            fontSize: 18,
                            fontFamily: "MarcellusRegular, serif",
                            textTransform: "none",
                            borderRadius: 4,
                            background: "#393d44",
                            color: "#faef9b",
                            boxShadow: "none",
                            "&:hover": {
                                background: "#393d44",
                                boxShadow: "none",
                            },
                        }}
                    >
                        Confirmer la modification
                    </Button>
                </ThemeProvider>
            </div>

        </div>
    );
}
